import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse
} from '@angular/common/http';
import { AuthService } from './auth.service';
import { DeviceFingerprintService } from '@app/shared/services/helpers/device-fingerprint.service';
import { Router } from '@angular/router';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { STATUS_DEVICE_PENDING, STATUS_DEVICE_RESTRICTED } from '../constants/LoginDeviceConstant';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    private refreshTokenInProgress = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
        null
    );

    constructor(
        public auth: AuthService,
        private deviceFingerprint: DeviceFingerprintService,
        private router: Router
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        request = this.addAuthToken(request);
        request = this.addDeviceFingerprint(request);

        return next.handle(request).pipe(catchError(error => {

            if (error instanceof HttpErrorResponse && error.status === 401) {
                return this.handle401Error(request, next);
            } else if (error instanceof HttpErrorResponse && error.status === 403) {
                return this.handle403Error(error);
            } else {
                return throwError(error);
            }

        }))

    }

    handle401Error(request: HttpRequest<any>, next: HttpHandler) {

        if (!this.refreshTokenInProgress) {

            this.refreshTokenInProgress = true;
            this.refreshTokenSubject.next(null);

            return this.auth.refreshToken().pipe(
                switchMap((token: any) => {
                    this.refreshTokenSubject.next(true);
                    let retryRequest = this.addAuthToken(request);
                    retryRequest = this.addDeviceFingerprint(retryRequest);
                    return next.handle(retryRequest);
                }),
                catchError((error) => {
                    this.refreshTokenSubject.next(false);
                    // The retry request (after a successful token refresh) may be blocked by
                    // device.check and return a 403. Since next.handle() bypasses this interceptor,
                    // handle403Error is never called for the retry — so we call it here.
                    if (error instanceof HttpErrorResponse && error.status === 403) {
                        return this.handle403Error(error);
                    }
                    return throwError(error);
                }),
                finalize(() => {
                    this.refreshTokenInProgress = false;
                })
            )

        } else {

            return this.refreshTokenSubject.pipe(
                filter(value => value !== null),
                take(1),
                switchMap(value => {
                    if (value === false) {
                        return throwError(new HttpErrorResponse({ status: 401 }));
                    }
                    let retryRequest = this.addAuthToken(request);
                    retryRequest = this.addDeviceFingerprint(retryRequest);
                    return next.handle(retryRequest);
                })
            )

        }

    }

    addAuthToken(request: HttpRequest<any>) {
        if (!this.auth) {
            return request;
        }
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${this.auth.getToken()}`
            }
        });

    }

    addDeviceFingerprint(request: HttpRequest<any>) {
        const deviceFingerprint = this.deviceFingerprint.getDeviceFingerprintHeader();

        if (!deviceFingerprint) {
            return request;
        }

        return request.clone({
            setHeaders: {
                'X-Device-Fingerprint': deviceFingerprint
            }
        });
    }

    handle403Error(error: HttpErrorResponse) {
        // Check if it's a device authorization error
        const errorMessage = error.error?.error || error.message || 'Access denied';

        // Check for device pending status
        if (errorMessage === STATUS_DEVICE_PENDING) {
            const token = error.error?.data?.token || error.error?.token;
            this.router.navigate(['/device-access-request'], {
                queryParams: {
                    deviceToken: token
                }
            });

            return throwError(error);
        }

        // Check for device restricted/revoked or not authorized
        if (errorMessage === STATUS_DEVICE_RESTRICTED) {
            // Redirect to device not authorized page
            this.router.navigate(['/device-not-authorized']);
            return throwError(error);
        }

        // For other 403 errors, return as is
        return throwError(error);
    }

}
